import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Typography, Tooltip } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import { PERIOD_SCALE_ENUM } from '~/modules/common/charts/timeline/periodScale';
import { KPI_TYPES } from '~/modules/common/charts/kpiTypes';
import { useMeContext } from '~/modules/me';
import {
  getReactMoneyFormatFromMe,
  getReactNumberFormatFromMe
} from '~/modules/common/numbers';
import OverviewItemTooltip from '~/modules/common/charts/dashboard/OverviewChart/OverviewItemTooltip';
import { formatDateRangeLabel } from '~/modules/common/dates/format';

const useStyles = makeStyles(theme => ({
  item: {
    display: 'flex',
    justifyContent: 'space-around',
    [theme.breakpoints.down('xs')]: {
      paddingTop: theme.spacing(2),
      justifyContent: 'center'
    }
  },
  container: {
    flexDirection: 'column',
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      minWidth: theme.spacing(17)
    }
  },
  label: {
    fontWeight: theme.typography.fontWeightMedium,
    fontFamily: theme.typography.subtitle1.fontFamily,
    color: theme.palette.text.secondary
  },
  row: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  value: {
    margin: theme.spacing(0.5, 0),
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary
  },
  notAvailable: {
    color: theme.palette.text.secondary,
    cursor: 'help'
  }
}));

const getSchedulePerformanceTooltip = ({
  tasksCount,
  formatMessage,
  isEntireProject,
  scale,
  dateRange
}) => {
  if (tasksCount === 0) {
    return formatMessage({
      id: 'projectKPI.schedulePerformanceIndex.naTooltip.noTasks'
    });
  }

  return formatMessage(
    {
      id: `projectKPI.schedulePerformanceIndex.naTooltip.${
        isEntireProject
          ? 'noPlannedEndDatesEntireProject'
          : 'noPlannedEndDatesPeriod'
      }`
    },
    {
      period: isEntireProject
        ? ''
        : formatDateRangeLabel(
            scale,
            dateRange.startDate,
            dateRange.endDate,
            formatMessage
          )
    }
  );
};

const getNotAvailableTooltip = ({
  tasksCount,
  type,
  formatMessage,
  isEntireProject,
  scale,
  dateRange
}) => {
  if (type === KPI_TYPES.SCHEDULEPERFORMANCEINDEX)
    return getSchedulePerformanceTooltip({
      tasksCount,
      formatMessage,
      isEntireProject,
      scale,
      dateRange
    });

  const kpiKey =
    type === KPI_TYPES.TIMESHEETCOMPLIANCEINDEX
      ? 'timesheetComplianceIndex'
      : 'billabilityIndex';

  return formatMessage(
    {
      id: `projectKPI.${kpiKey}.naTooltip.${
        isEntireProject ? 'entireProject' : 'period'
      }`
    },
    {
      period: isEntireProject
        ? ''
        : formatDateRangeLabel(
            scale,
            dateRange.startDate,
            dateRange.endDate,
            formatMessage
          )
    }
  );
};

const ProjectKPIItem = ({
  title,
  value,
  type,
  scale,
  dateRange,
  dataQeId,
  tasksCount
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { me } = useMeContext();

  const moneySettings = getReactMoneyFormatFromMe(me);
  const numberSettings = getReactNumberFormatFromMe(me);

  const isEntireProject = scale === PERIOD_SCALE_ENUM.ENTIRE_PROJECT;
  const tooltipTitle = getNotAvailableTooltip({
    tasksCount,
    type,
    formatMessage,
    isEntireProject,
    scale,
    dateRange
  });

  const renderValue = () => {
    if (value !== null) {
      return (
        <OverviewItemTooltip
          value={value}
          moneySettings={moneySettings}
          numberSettings={numberSettings}
          intlKey="cost"
          id="dashboardOverviewChart.costDescription"
          isPercentage
        />
      );
    }

    return (
      <Tooltip title={tooltipTitle} arrow enterTouchDelay={0}>
        <span className={classNames(classes.value, classes.notAvailable)}>
          <FormattedMessage id="dashboardOverviewChart.na" />
        </span>
      </Tooltip>
    );
  };

  return (
    <div className={classes.item}>
      <div className={classes.container}>
        <Typography className={classes.label} data-qe-id={dataQeId}>
          {title}
        </Typography>
        <div className={classes.row}>
          <Typography className={classes.value} data-qe-id={dataQeId}>
            {renderValue()}
          </Typography>
        </div>
      </div>
    </div>
  );
};

ProjectKPIItem.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.number,
  type: PropTypes.oneOf(Object.values(KPI_TYPES)).isRequired,
  scale: PropTypes.string,
  dateRange: PropTypes.object,
  dataQeId: PropTypes.string,
  tasksCount: PropTypes.number
};

export default ProjectKPIItem;
